// TODO: this should be in it's own package (Copied from RosterFrontEndStaticWebsiteReact)
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { useState, useReducer, useEffect, useCallback } from "react";
import { PayloadAction, Action } from "redux-starter-kit";
import { MetricsState, useMetricsPublisher, MetricType } from "../logging";
import LoadingState from "./LoadingState";
import Api from "./Api";
import http from "./http";
import EnhancedAxiosError from "./EnhancedAxiosError";
import { RequiredAxiosRequestConfig } from "./axiosModel";

const isAbsolutePathRegex = /^[a-z][a-z\d+.-]*:/;

const api = new Api(process.env.API_BASE_URL);

/**
 * Defines request action types for useHttpRequest hook reducer.
 *
 * @enum {number}
 */
enum RequestActions {
  REQUEST_INIT = "REQUEST_INIT",
  REQUEST_SUCCESS = "REQUEST_SUCCESS",
  REQUEST_FAILURE = "REQUEST_FAILURE",
  REQUEST_RESET = "REQUEST_RESET",
}

/**
 * Interface for useHttpRequest hook state.
 *
 * @interface UseHttpRequestState
 * @template T
 */
export interface UseHttpRequestState<T> {
  loadingState: LoadingState;
  response?: AxiosResponse<T>;
  data?: T;
  error?: EnhancedAxiosError;
}

/**
 * Interface defining hook actions.
 *
 * @interface UseHttpRequestActions
 */
interface UseHttpRequestActions {
  sendRequest: (requestConfig: RequiredAxiosRequestConfig) => void;
  reset: () => void;
}

const initialState = {
  loadingState: LoadingState.NO_DATA,
  response: undefined,
  data: undefined,
  error: undefined,
};

/**
 * Reducer for useHttpRequest hook.
 *
 * @template T
 * @param {UseHttpRequestState<T>} state
 * @param {(Action<string> | PayloadAction)} action
 * @returns {UseHttpRequestState<T>}
 */
function httpRequestReducer<T>(
  state: UseHttpRequestState<T>,
  action: Action<string> | PayloadAction
): UseHttpRequestState<T> {
  switch (action.type) {
    case RequestActions.REQUEST_INIT:
      return {
        ...state,
        loadingState: LoadingState.LOADING,
        error: undefined,
      };
    case RequestActions.REQUEST_SUCCESS: {
      const payloadAction = action as PayloadAction<AxiosResponse<T>>;
      return {
        ...state,
        loadingState: LoadingState.RESOLVED,
        response: payloadAction.payload,
        data: payloadAction.payload.data,
      };
    }
    case RequestActions.REQUEST_FAILURE: {
      const payloadAction = action as PayloadAction<EnhancedAxiosError>;
      return {
        ...state,
        loadingState: LoadingState.REJECTED,
        error: payloadAction.payload,
      };
    }
    case RequestActions.REQUEST_RESET:
      return {
        ...state,
        ...initialState,
      };
    default:
      throw new Error("Invalid action.");
  }
}

/**
 * React hook that encapsulates an http request.
 * Returns loading state + any response data/errors.
 *
 * @template T
 * @param {string} actionName
 * @param {MetricsState} metricsState
 * @param {RequiredAxiosRequestConfig} [config]
 * @returns {(UseHttpRequestState<T> & {
 *   sendRequest: (requestConfig: RequiredAxiosRequestConfig) => void;
 * })}
 */
const useHttpRequest = <T>(
  actionName: string,
  metricsState: MetricsState,
  config?: RequiredAxiosRequestConfig
): UseHttpRequestState<T> & UseHttpRequestActions => {
  // Set local state for axios config.
  // Used to determine new request should be sent.
  const [axiosConfig, setAxiosConfig] = useState<
    AxiosRequestConfig | undefined
  >(config);

  // set metrics state as state to prevent dependency craziness
  const [localMetricsState] = useState<MetricsState>(metricsState);

  // Creates reducer using react useReducer hook.
  const [state, dispatch] = useReducer<
    React.Reducer<UseHttpRequestState<T>, Action<string> | PayloadAction>
  >(httpRequestReducer, initialState);

  // Effect runs every time axiosConfig changes.
  useEffect(() => {
    let didCancel = false;

    const metricsPublisher = useMetricsPublisher(localMetricsState, actionName);

    // Function that actually makes the request.
    // Use effect cannot be async, so this function must be declared inside.
    const asyncRequestFn = async (): Promise<void> => {
      if (!axiosConfig || !axiosConfig.url) {
        // Do not send requests without a config or url.
        return;
      }

      // Url prefixed with api endpoint url if request is relative.
      const url = !isAbsolutePathRegex.test(axiosConfig.url)
        ? api.getEndpointUri(axiosConfig.url)
        : axiosConfig.url;

      const transformedConfig = {
        ...axiosConfig,
        url,
      };

      console.log("starting async http request");

      // At the onset of loading dispatch request init action.
      dispatch({ type: RequestActions.REQUEST_INIT });

      const httpMetric = new MetricType.HttpRequest(actionName).withMonitor();
      httpMetric.url = transformedConfig.url;

      try {
        const response = await http.request<T>(transformedConfig);

        httpMetric.statusCode = response.status;
        httpMetric.statusText = response.statusText;
        httpMetric.setSuccess();

        if (!didCancel) {
          // Dispatch request success with axios response as payload.
          dispatch({ type: RequestActions.REQUEST_SUCCESS, payload: response });
        }
      } catch (error) {
        httpMetric.setFailure();

        const httpError = error as EnhancedAxiosError;
        if (httpError.response) {
          httpMetric.statusCode = httpError.response.status;
          httpMetric.statusText = httpError.response.statusText;
        } else {
          console.error(error);
        }

        if (!didCancel) {
          dispatch({ type: RequestActions.REQUEST_FAILURE, payload: error });
        }
      } finally {
        metricsPublisher.publish(httpMetric);
        console.log("ending async http request.");
      }
    };

    // Initiate request function.
    asyncRequestFn();

    return () => {
      // On unmount set request as cancelled.
      didCancel = true;
    };
  }, [axiosConfig, actionName, localMetricsState]);

  // Function that allows component to send a request.
  const sendRequest = useCallback(
    (requestConfig: RequiredAxiosRequestConfig): void => {
      setAxiosConfig(requestConfig);
    },
    []
  );

  // allow component to reset the state.
  const reset = useCallback((): void => {
    setAxiosConfig(undefined);
    dispatch({ type: RequestActions.REQUEST_RESET });
  }, []);

  return { ...state, sendRequest, reset };
};

export default useHttpRequest;
